import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/stores/RootStore';
import { authApi } from '@/services/authApi';
import AuthShell from './AuthShell';
import s from './Auth.module.scss';

/**
 * [2026-09-10] 로그인 — evs.com식 다크 split. 아이디(또는 이메일)+비밀번호.
 *   성공 시 role 분기: ADMIN→/admin, USER→직전 페이지(또는 홈). 단일세션 만료 안내도 표시.
 */
function Login() {
	const auth = useAuth();
	const nav = useNavigate();
	const loc = useLocation() as { state?: { from?: string } };
	const [loginId, setLoginId] = useState('');
	const [password, setPassword] = useState('');
	const [remember, setRemember] = useState(false);
	const [err, setErr] = useState<string | null>(null);
	const [fe, setFe] = useState<{ loginId?: boolean; password?: boolean }>({});
	const [busy, setBusy] = useState(false);

	// 아이디 저장 — 로컬 저장된 아이디 프리필
	useEffect(() => {
		try { const v = localStorage.getItem('evs_saved_loginid'); if (v) { setLoginId(v); setRemember(true); } } catch { /* private */ }
	}, []);

	async function submit(e: React.FormEvent) {
		e.preventDefault();
		setErr(null);
		const fx: { loginId?: boolean; password?: boolean } = {};
		if (!loginId.trim()) fx.loginId = true;
		if (!password) fx.password = true;
		setFe(fx);
		if (fx.loginId || fx.password) return;   // 빈 필드 → 빨간 테두리
		setBusy(true);
		const msg = await auth.login(loginId.trim(), password);
		setBusy(false);
		if (msg) { setErr(msg); return; }
		try { if (remember) localStorage.setItem('evs_saved_loginid', loginId.trim()); else localStorage.removeItem('evs_saved_loginid'); } catch { /* private */ }
		if (auth.isAdmin) nav('/admin', { replace: true });
		else nav(loc.state?.from || '/', { replace: true });
	}

	async function startSocial(provider: 'google' | 'naver') {
		setErr(null);
		const r = await authApi.socialAuthorize(provider);
		if (r.success && r.url) window.location.href = String(r.url);
		else setErr(r.message || '소셜 로그인을 시작할 수 없습니다.');
	}

	return (
		<AuthShell eyebrow="EVS MEMBERSHIP" title={<>로그인</>}
			desc="안전과 환경을 잇는 EVS 회원 서비스입니다. 아이디 또는 이메일로 로그인해주세요.">
			{auth.sessionEndedMsg && <div className={`${s.msg} ${s.error}`}>{auth.sessionEndedMsg}</div>}
			{err && <div className={`${s.msg} ${s.error}`}>{err}</div>}
			<form onSubmit={submit}>
				<div className={s.field}>
					<label className={s.label}>아이디 또는 이메일</label>
					<input className={`${s.input} ${fe.loginId ? s.err : ''}`} value={loginId}
						onChange={(e) => { setLoginId(e.target.value); if (fe.loginId) setFe((f) => ({ ...f, loginId: false })); }}
						placeholder="아이디 또는 이메일" autoComplete="username" />
					{fe.loginId && <p className={`${s.hint} ${s.bad}`}>아이디 또는 이메일을 입력해주세요.</p>}
				</div>
				<div className={s.field}>
					<label className={s.label}>비밀번호</label>
					<input className={`${s.input} ${fe.password ? s.err : ''}`} type="password" value={password}
						onChange={(e) => { setPassword(e.target.value); if (fe.password) setFe((f) => ({ ...f, password: false })); }}
						placeholder="비밀번호" autoComplete="current-password" />
					{fe.password && <p className={`${s.hint} ${s.bad}`}>비밀번호를 입력해주세요.</p>}
				</div>
				<label className={s.termRow} style={{ padding: '2px 0 4px' }}>
					<input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
					<span>아이디 저장</span>
				</label>
				<button className={s.primary} disabled={busy}>
					{busy ? '로그인 중…' : '로그인'}
				</button>
			</form>

			<div className={s.socialDivider}>또는 소셜 계정으로</div>
			<div className={s.socialBtns}>
				<button type="button" className={`${s.socialBtn} ${s.socialGoogle}`} onClick={() => startSocial('google')}>
					<svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
						<path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
						<path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
						<path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
						<path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
					</svg> 구글 로그인 하기
				</button>
				<button type="button" className={`${s.socialBtn} ${s.socialNaver}`} onClick={() => startSocial('naver')}>
					<span className={s.socialIcon}>N</span> 네이버 로그인 하기
				</button>
			</div>
			{/* [2026-09-12 사용자] 소셜 로그인 안내 — 개발자에게도 비직관적이라 명시.
			    핵심: 소셜은 '이미 구글/네이버에 그 이메일로 가입된 계정'으로만 로그인/가입됨(웹사이트 유무와 무관).
			    처음이면 우리 DB에 신규 회원으로 생성되고, 휴대폰 본인인증을 거쳐야 가입이 완료됨. */}
			<p className={s.hint} style={{ textAlign: 'center', margin: '12px 4px 0', lineHeight: 1.6 }}>
				이미 구글·네이버에 가입된 계정으로 로그인합니다. 처음이시면 자동으로 회원가입되며, <b>휴대폰 본인인증</b>을 완료해야 가입이 마무리됩니다.
			</p>

			<div className={s.links}>
				<Link to="/signup">회원가입</Link>
				<span className={s.sep}>·</span>
				<Link to="/find-account">아이디·비밀번호 찾기</Link>
			</div>
		</AuthShell>
	);
}

export default observer(Login);
